import get from 'lodash.get';
import { sendOnApprovalRequest } from 'dibs-trade-on-approval/exports/sendOnApprovalRequest';
import { sendMessageMutation } from '../mutations/sendMessageMutation';
import { holdRequestMutation } from '../mutations/holdRequestMutation';
import { dblUserAddAddressMutation } from '../mutations/dblUserAddAddressMutation';
import { requestShipmentQuoteMutation } from '../mutations/requestShipmentQuoteMutation';
import * as trackingHelpers from './tracking';

export const ACTION_TYPES = {
    gotoModalStep: 'GOTO_MODAL_STEP',
    messageSuccess: 'MESSAGE_SENT',
    resetContactData: 'RESET_CONTACT_DATA',
    setTaggedProjectName: 'SET_TAGGED_PROJECT_NAME',
    onApprovalRequestSent: 'ON_APPROVAL_REQUEST_SENT',
    setServiceError: 'SET_SERVICE_ERROR',
    item: 'CONTACT_SELLER_PDP',
    seller: 'CONTACT_SELLER_STOREFRONT',
    contactDealer: 'CONTACT_DEALER',
    requestHold: 'HOLD_REQUEST',
    shippingRequest: 'SHIPPING_REQUEST',
    shippingHoldRequest: 'SHIPPING_HOLD_REQUEST',
};

const messageSuccess = (payload = {}) => ({
    type: ACTION_TYPES.messageSuccess,
    ...payload,
});

const errorHandler = (hasInvalidCharacterError = false) => ({
    type: ACTION_TYPES.setServiceError,
    hasInvalidCharacterError,
});

export const goToStep = ({ step, isBackLink, modalContentData }) => ({
    type: ACTION_TYPES.gotoModalStep,
    currentStep: step,
    isBackLink: isBackLink,
    modalContentData,
});

const sendContactMessage = (inputPayload, mutationArgs) => () => {
    const { action, context } = mutationArgs;
    const { item, onFailure, onSuccess, relay, seller, user, message } = inputPayload;

    return sendMessageMutation({
        environment: relay.environment,
        action,
        pageContext: context,
        sellerPk: get(seller, 'serviceId'),
        buyerId: get(user, 'serviceId'),
        itemPk: get(item, 'serviceId'),
        messageContent: message,
    })
        .then(onSuccess)
        .catch(onFailure);
};

const sendHoldRequest = inputPayload => () => {
    const { user, seller, item, duration, relay, onSuccess, onFailure } = inputPayload;
    return holdRequestMutation(relay.environment, {
        userId: user.serviceId,
        sellerId: seller.serviceId,
        itemId: item.serviceId,
        vertical: item.vertical,
        duration,
        onSuccess,
        onFailure,
    })
        .then(onSuccess)
        .catch(onFailure);
};

const goToSuccess = ({
    chatSuccessTag,
    chatTracking,
    guidedPromptSelection,
    holdRequestFieldValue,
    optimizelyVariant,
    trackingModalName,
    tracking,
}) => (dispatch, getState) => {
    const trackingParams = tracking || getState().tracking;

    dispatch(
        goToStep({
            modalContentData: {
                copySuffix: '',
                headerSuffix: '',
            },
            step: 'confirmation',
        })
    );

    let trackVariantAfterSubmission;

    if (typeof Storage !== 'undefined') {
        trackVariantAfterSubmission = sessionStorage.getItem('trackVariantAfterSubmission');
    }

    trackingHelpers.eventContactSuccess({
        ...trackingParams,
        trackVariant: trackVariantAfterSubmission,
        guidedPromptSelection,
        optimizelyVariant,
    });

    sessionStorage.removeItem('trackVariantAfterSubmission');

    if (holdRequestFieldValue) {
        trackingHelpers.eventHoldRequestSubmitted({ sourceModal: trackingModalName });
    }

    const trackChatEvent = chatTracking ? chatTracking[chatSuccessTag] : false;
    if (trackChatEvent) {
        trackChatEvent();
    }
};

const userAddAddress = inputPayload => () => {
    const { relay, userId, address, onSuccess, onFailure } = inputPayload;
    return dblUserAddAddressMutation(relay.environment, {
        userId,
        address,
    })
        .then(onSuccess)
        .catch(onFailure);
};

const requestShipmentQuote = inputPayload => () => {
    const {
        relay,
        userId,
        itemId,
        dealerPk,
        action,
        subject,
        message,
        address,
        onSuccess,
        onFailure,
    } = inputPayload;

    return requestShipmentQuoteMutation(relay.environment, {
        userId,
        itemId,
        dealerPk,
        action,
        subject,
        message,
        address,
    })
        .then(onSuccess)
        .catch(onFailure);
};

function getSubject({ item, copy, emailSubject }) {
    const subjectItem = (copy && copy.subjectItem) || '';
    const subjectDealer = (copy && copy.subjectDealer) || '';

    let subject = item ? [subjectItem, item.title, item.dibsReference].join(' - ') : subjectDealer;
    subject = emailSubject ? emailSubject : subject;

    return subject;
}

const _sendMessage = inputPayload => (dispatch, getState) => {
    const {
        item,
        subject: emailSubject,
        copy,
        holdRequestedFromConfirmationModal,
        action = '',
        hasHoldRequest,
        requestOnApprovalSubmitted,
        message,
    } = inputPayload;
    const { holdRequestFieldValue } = getState().fields || {};

    const containsMessage = message && message.length;
    const subject = getSubject({ item, copy, emailSubject });
    const mutationArgs = {
        action:
            action ||
            (holdRequestFieldValue ? ACTION_TYPES.requestHold : ACTION_TYPES.contactDealer),
        context: item ? ACTION_TYPES.item : ACTION_TYPES.seller,
        subject,
    };

    if (hasHoldRequest || holdRequestFieldValue) {
        dispatch(
            sendHoldRequest({
                onSuccess: () => {
                    dispatch(
                        sendContactMessage(
                            {
                                onSuccess: () => {
                                    dispatch(
                                        messageSuccess({
                                            messageType: action || ACTION_TYPES.requestHold,
                                        })
                                    );
                                    dispatch(goToSuccess(inputPayload));
                                },
                                onFailure: hasInvalidCharacterError => {
                                    return dispatch(errorHandler(hasInvalidCharacterError));
                                },
                                ...inputPayload,
                            },
                            mutationArgs
                        )
                    );

                    //this event tells tracking that the user requested a hold by clicking on "request a hold" in the confiramtion modal for a prior message
                    if (holdRequestedFromConfirmationModal) {
                        trackingHelpers.convertedHoldRequestFromConfirmationModal();
                    }
                },
                onFailure: () => dispatch(errorHandler()),
                ...inputPayload,
            })
        );
    } else if (requestOnApprovalSubmitted && !containsMessage) {
        dispatch(goToSuccess(inputPayload));
    } else {
        dispatch(
            sendContactMessage(
                {
                    onSuccess: () => {
                        dispatch(
                            messageSuccess({
                                messageType: action || ACTION_TYPES.contactDealer,
                            })
                        );
                        dispatch(goToSuccess(inputPayload));
                    },
                    onFailure: hasInvalidCharacterErrors => {
                        return dispatch(errorHandler(hasInvalidCharacterErrors));
                    },
                    ...inputPayload,
                },
                mutationArgs
            )
        );
    }
};

const requestShippingQuoteAction = inputPayload => dispatch => {
    const { relay, user, item, action, subject, message, onSuccess } = inputPayload;
    const address = get(inputPayload, 'data.requestShippingQuote.address');
    const isNewAddress = address && !address.serviceId;

    const requestShippingQuotePayload = {
        relay,
        userId: get(user, 'serviceId'),
        itemId: get(item, 'serviceId'),
        dealerPk: get(item, 'seller.serviceId'),
        action,
        subject,
        message,
        address,
        onSuccess,
        onFailure: () => dispatch(errorHandler()),
    };

    if (isNewAddress) {
        return dispatch(
            userAddAddress({
                relay: inputPayload.relay,
                userId: inputPayload.userId,
                address,
                onSuccess: payload =>
                    dispatch(
                        requestShipmentQuote({
                            ...requestShippingQuotePayload,
                            // update address with address saved for user
                            address: get(payload, 'userAddAddress.address'),
                        })
                    ),
                onFailure: () => dispatch(errorHandler()),
            })
        );
    }

    return dispatch(requestShipmentQuote(requestShippingQuotePayload));
};

export const submitMultiRequestContactDealer = inputPayload => dispatch => {
    const {
        item,
        copy,
        subject: emailSubject,
        taggedProjectName,
        onSuccessfulShippingQuoteRequest = () => {},
    } = inputPayload;

    const {
        requestHold,
        requestShippingQuote,
        additionalQuestions,
        requestOnApproval,
    } = inputPayload.data;
    let action = ACTION_TYPES.contactDealer;
    let hasHoldRequest = false;
    const requestHoldSubmitted = requestHold && requestHold.checked && requestHold.isValid;
    const requestShippingQuoteSubmitted =
        requestShippingQuote && requestShippingQuote.checked && requestShippingQuote.isValid;

    const requestOnApprovalSubmitted =
        requestOnApproval && requestOnApproval.checked && requestOnApproval.isValid;

    const subject = getSubject({ item, copy, emailSubject });
    const message = additionalQuestions && additionalQuestions.value;
    let duration = null;

    if (requestHoldSubmitted) {
        action = ACTION_TYPES.requestHold;
        hasHoldRequest = true;
        duration = requestHold.days > 0 ? requestHold.days : null;
    }

    if (requestShippingQuoteSubmitted) {
        action = ACTION_TYPES.shippingRequest;
    }

    if (requestHoldSubmitted && requestShippingQuoteSubmitted) {
        action = ACTION_TYPES.shippingHoldRequest;
    }

    // if a project was tagged, the confrmation modal needs to know
    if (taggedProjectName) {
        dispatch({ type: ACTION_TYPES.setTaggedProjectName, taggedProjectName });
    }

    if (requestOnApprovalSubmitted) {
        sendOnApprovalRequest(requestOnApproval.data);
        dispatch({ type: ACTION_TYPES.onApprovalRequestSent });
    }

    const onMessageSuccess = () => {
        dispatch({
            type: ACTION_TYPES.messageSuccess,
            messageType: action,
        });
        dispatch(goToSuccess(inputPayload)); // goes to confirmation
    };

    const messagePayload = {
        ...inputPayload,
        subject,
        message,
        action,
        hasHoldRequest,
        requestOnApprovalSubmitted,
        duration,
    };

    if (requestHoldSubmitted && requestShippingQuoteSubmitted) {
        return dispatch(
            requestShippingQuoteAction({
                ...messagePayload,
                onSuccess: () => {
                    onSuccessfulShippingQuoteRequest();
                    dispatch(
                        sendHoldRequest({
                            ...inputPayload,
                            duration,
                            onSuccess: onMessageSuccess,
                            onFailure: () => dispatch(errorHandler()),
                        })
                    );
                },
            })
        );
    } else if (requestShippingQuoteSubmitted) {
        return dispatch(
            requestShippingQuoteAction({
                ...messagePayload,
                onSuccess: () => {
                    onSuccessfulShippingQuoteRequest();
                    onMessageSuccess();
                },
            })
        );
    }

    return dispatch(_sendMessage(messagePayload));
};
